import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { X } from "lucide-react";

const EMPTY = {
  name: "", whatsapp: "", phone: "", email: "", birth_date: "",
  address: "", city: "", notes: "", status: "PROSPECTO", tags: [], photo: "",
};

export default function ClientFormDialog({ open, onOpenChange, client, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [allTags, setAllTags] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(client ? { ...EMPTY, ...client } : EMPTY);
      api.get("/tags").then((r) => setAllTags(r.data));
    }
  }, [open, client]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleTag = (t) =>
    setForm((f) => ({ ...f, tags: f.tags.includes(t) ? f.tags.filter((x) => x !== t) : [...f.tags, t] }));

  const save = async () => {
    if (!form.name.trim()) return toast.error("Informe o nome do cliente.");
    setSaving(true);
    try {
      const payload = { ...form };
      if (client?.id) await api.put(`/clients/${client.id}`, payload);
      else await api.post("/clients", payload);
      toast.success(client ? "Cliente atualizado!" : "Cliente cadastrado!");
      onOpenChange(false);
      onSaved && onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="client-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{client ? "Editar Cliente" : "Novo Cliente"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
          <div className="sm:col-span-2">
            <Label>Nome completo *</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" data-testid="client-name-input" />
          </div>
          <div>
            <Label>WhatsApp</Label>
            <Input value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} placeholder="(83) 99999-9999" className="mt-1.5" data-testid="client-whatsapp-input" />
          </div>
          <div>
            <Label>Telefone</Label>
            <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1.5" />
          </div>
          <div>
            <Label>E-mail</Label>
            <Input value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5" data-testid="client-email-input" />
          </div>
          <div>
            <Label>Data de nascimento</Label>
            <Input type="date" value={form.birth_date} onChange={(e) => set("birth_date", e.target.value)} className="mt-1.5" />
          </div>
          <div>
            <Label>Endereço</Label>
            <Input value={form.address} onChange={(e) => set("address", e.target.value)} className="mt-1.5" />
          </div>
          <div>
            <Label>Cidade</Label>
            <Input value={form.city} onChange={(e) => set("city", e.target.value)} className="mt-1.5" />
          </div>
          <div>
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger className="mt-1.5" data-testid="client-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ATIVO">Ativo</SelectItem>
                <SelectItem value="INATIVO">Inativo</SelectItem>
                <SelectItem value="PROSPECTO">Prospecto</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <Label>Observações</Label>
            <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} className="mt-1.5" rows={2} />
          </div>
          <div className="sm:col-span-2">
            <Label>Tags</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {allTags.map((t) => (
                <button
                  key={t.id} type="button" onClick={() => toggleTag(t.name)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                    form.tags.includes(t.name) ? "bg-emerald-500 text-white border-emerald-500" : "bg-white text-slate-600 border-slate-200 hover:border-emerald-300"
                  }`}
                >
                  {t.name}
                </button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={save} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600" data-testid="client-save-button">
            {saving ? "Salvando..." : "Salvar Cliente"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
